import { useState } from 'react';
import {
  useCompanyTemplates,
  useCreateCompanyTemplate,
  useUpdateCompanyTemplate,
  useDeleteCompanyTemplate,
} from '../../hooks/unified/useJobTemplatesQueries';
import { useToast } from '../../contexts/ToastContext';
import { EducationalQualificationsSection } from './EducationalQualificationsSection';
import type { JobTemplate, CreateJobTemplateRequest, JobTemplateType } from '../../services/unified/types';
import { isAITemplate } from '../../services/unified/types';

interface CompanyTemplatesManagerProps {
  companyId: string;
}

export function CompanyTemplatesManager({ companyId }: CompanyTemplatesManagerProps) {
  const { data: templatesData, isLoading } = useCompanyTemplates(companyId);
  // Backend returns { systemTemplates: [], companyTemplates: [], aiTemplates: [] }
  // Combine companyTemplates and aiTemplates for display
  const templates = Array.isArray(templatesData) 
    ? templatesData 
    : [
        ...((templatesData as any)?.companyTemplates || []),
        ...((templatesData as any)?.aiTemplates || []),
      ];
  const createMutation = useCreateCompanyTemplate();
  const updateMutation = useUpdateCompanyTemplate();
  const deleteMutation = useDeleteCompanyTemplate();
  const { showSuccess, showError } = useToast();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<JobTemplate | null>(null);

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setShowCreateModal(true);
  };

  const handleEditTemplate = (template: JobTemplate) => {
    setEditingTemplate(template);
    setShowCreateModal(true);
  };

  const handleDeleteTemplate = async (template: JobTemplate) => {
    if (!confirm(`Are you sure you want to delete the template "${template.name}"?`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync({
        companyId,
        templateId: template.id,
      });
      showSuccess('Template deleted successfully');
    } catch (error) {
      showError('Failed to delete template');
      console.error('Delete template error:', error);
    }
  };

  const handleSaveTemplate = async (data: CreateJobTemplateRequest) => {
    try {
      if (editingTemplate) {
        // Prepare update data based on template type
        const updateData: any = { name: data.name };
        if (data.template_type === 'ai') {
          updateData.ai_context = data.ai_context;
        } else {
          updateData.job_data = data.job_data;
        }
        
        await updateMutation.mutateAsync({
          companyId,
          templateId: editingTemplate.id,
          data: updateData,
        });
        showSuccess('Template updated successfully');
      } else {
        await createMutation.mutateAsync({
          companyId,
          data,
        });
        showSuccess('Template created successfully');
      }
      setShowCreateModal(false);
      setEditingTemplate(null);
    } catch (error) {
      showError(editingTemplate ? 'Failed to update template' : 'Failed to create template');
      console.error('Save template error:', error);
    }
  };

  const handleDuplicateTemplate = async (template: JobTemplate) => {
    try {
      const newName = `${template.name} (Copy)`;
      const duplicateData: CreateJobTemplateRequest = {
        name: newName,
        template_type: template.template_type,
      };

      // Copy ai_context for AI templates, job_data for regular templates
      if (isAITemplate(template)) {
        duplicateData.ai_context = template.ai_context;
      } else {
        duplicateData.job_data = typeof template.job_data === 'string' 
          ? JSON.parse(template.job_data) 
          : template.job_data;
      }

      await createMutation.mutateAsync({
        companyId,
        data: duplicateData,
      });
      showSuccess('Template duplicated successfully');
    } catch (error) {
      showError('Failed to duplicate template');
      console.error('Duplicate template error:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Job Templates</h2>
          <button
            onClick={handleCreateTemplate}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Template
          </button>
        </div>

        {templates.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No templates</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a job template for this company
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreateTemplate}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Template
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* AI Templates Section */}
            {templates.filter((t: JobTemplate) => isAITemplate(t)).length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-purple-700 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  AI Templates
                </h3>
                <div className="space-y-3">
                  {templates.filter((t: JobTemplate) => isAITemplate(t)).map((template: JobTemplate) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onEdit={handleEditTemplate}
                      onDelete={handleDeleteTemplate}
                      onDuplicate={handleDuplicateTemplate}
                      isDeleting={deleteMutation.isPending}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Regular Templates Section */}
            {templates.filter((t: JobTemplate) => !isAITemplate(t)).length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Regular Templates
                </h3>
                <div className="space-y-3">
                  {templates.filter((t: JobTemplate) => !isAITemplate(t)).map((template: JobTemplate) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onEdit={handleEditTemplate}
                      onDelete={handleDeleteTemplate}
                      onDuplicate={handleDuplicateTemplate}
                      isDeleting={deleteMutation.isPending}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showCreateModal && (
        <TemplateModal
          companyId={companyId}
          template={editingTemplate}
          onSave={handleSaveTemplate}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </>
  );
}

// Template card component for displaying templates in the list
interface TemplateCardProps {
  template: JobTemplate;
  onEdit: (template: JobTemplate) => void;
  onDelete: (template: JobTemplate) => void;
  onDuplicate: (template: JobTemplate) => void;
  isDeleting: boolean;
}

function TemplateCard({ template, onEdit, onDelete, onDuplicate, isDeleting }: TemplateCardProps) {
  const isAI = isAITemplate(template);
  
  // Get display title - for AI templates, show context preview; for regular, show job title
  const getDisplayInfo = () => {
    if (isAI) {
      const context = template.ai_context;
      if (context?.title_context) {
        return context.title_context.substring(0, 50) + (context.title_context.length > 50 ? '...' : '');
      }
      return 'AI-powered template';
    }
    
    const jobData = typeof template.job_data === 'string' 
      ? JSON.parse(template.job_data) 
      : template.job_data;
    return jobData?.title || 'No title';
  };

  return (
    <div
      className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
        isAI ? 'border-purple-200 bg-purple-50/30' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
            {isAI && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
            <span className="inline-flex items-center">
              {isAI ? (
                <svg className="w-4 h-4 mr-1 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              )}
              {getDisplayInfo()}
            </span>
            <span>
              Created {new Date(template.created_at || '').toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={() => onDuplicate(template)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
            title="Duplicate template"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            onClick={() => onEdit(template)}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-md"
            title="Edit template"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(template)}
            disabled={isDeleting}
            className="p-2 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
            title="Delete template"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// Simple template modal component
interface TemplateModalProps {
  companyId: string;
  template: JobTemplate | null;
  onSave: (data: CreateJobTemplateRequest) => void;
  onClose: () => void;
  isSaving: boolean;
}

function TemplateModal({ template, onSave, onClose, isSaving }: TemplateModalProps) {
  const existingData = template && typeof template.job_data === 'string' 
    ? JSON.parse(template.job_data)
    : template?.job_data || {};

  // Determine if editing an AI template
  const isEditingAITemplate = template ? isAITemplate(template) : false;

  const [name, setName] = useState(template?.name || '');
  const [isAITemplateType, setIsAITemplateType] = useState(isEditingAITemplate);
  
  // Regular template fields
  const [title, setTitle] = useState(existingData.title || '');
  const [description, setDescription] = useState(existingData.description || '');
  const [location, setLocation] = useState(existingData.location || '');
  const [salaryMin, setSalaryMin] = useState(existingData.salary_min || '');
  const [salaryMax, setSalaryMax] = useState(existingData.salary_max || '');
  const [jobType, setJobType] = useState(existingData.job_type || 'full-time');
  const [experienceLevel, setExperienceLevel] = useState(existingData.experience_level || 'mid');
  const [requirements, setRequirements] = useState<string[]>(existingData.requirements || []);
  const [benefits, setBenefits] = useState<string[]>(existingData.benefits || []);
  const [educationalQualifications, setEducationalQualifications] = useState<any[]>(existingData.educational_qualifications || []);
  const [requirementInput, setRequirementInput] = useState('');
  const [benefitInput, setBenefitInput] = useState('');

  // Default AI prompts for new templates
  const defaultPrompts = {
    title: `Generate a professional job title for this position. Consider:
- The role's primary function and level (e.g., Senior, Junior, Lead)
- Industry-specific terminology
- Keep it concise but descriptive (3-6 words)`,
    description: `Create a comprehensive job description that includes:
- A compelling overview of the role and its impact
- Key responsibilities (5-7 bullet points)
- What makes this opportunity unique
- Team/department context
Use professional but engaging language.`,
    requirements: `List the job requirements including:
- Required qualifications (education, certifications)
- Years of experience needed
- Technical skills and competencies
- Soft skills (communication, leadership, etc.)
Separate into "Required" and "Preferred" categories.`,
    benefits: `Generate attractive job benefits including:
- Compensation and bonuses
- Health and wellness benefits
- Work-life balance perks (remote work, flexible hours)
- Professional development opportunities
- Unique company perks`,
    educational: `Specify educational requirements:
- Minimum degree level required
- Preferred fields of study
- Preferred institutions (if applicable)
- Additional certifications or training`,
  };

  // AI template context fields - use defaults for new templates
  const existingContext = template?.ai_context || {};
  const [titleContext, setTitleContext] = useState(
    existingContext.title_context || (template ? '' : defaultPrompts.title)
  );
  const [descriptionContext, setDescriptionContext] = useState(
    existingContext.description_context || (template ? '' : defaultPrompts.description)
  );
  const [requirementsContext, setRequirementsContext] = useState(
    existingContext.requirements_context || (template ? '' : defaultPrompts.requirements)
  );
  const [benefitsContext, setBenefitsContext] = useState(
    existingContext.benefits_context || (template ? '' : defaultPrompts.benefits)
  );
  const [educationalContext, setEducationalContext] = useState(
    existingContext.educational_qualifications_context || (template ? '' : defaultPrompts.educational)
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isAITemplateType) {
      // AI template - save context prompts
      onSave({
        name,
        template_type: 'ai' as JobTemplateType,
        ai_context: {
          title_context: titleContext || undefined,
          description_context: descriptionContext || undefined,
          requirements_context: requirementsContext || undefined,
          benefits_context: benefitsContext || undefined,
          educational_qualifications_context: educationalContext || undefined,
        },
      });
    } else {
      // Regular template - save job data
      onSave({
        name,
        template_type: 'custom' as JobTemplateType,
        job_data: {
          title,
          description,
          location,
          salary_min: salaryMin ? parseInt(salaryMin as any) : undefined,
          salary_max: salaryMax ? parseInt(salaryMax as any) : undefined,
          job_type: jobType,
          experience_level: experienceLevel,
          requirements,
          benefits,
          educational_qualifications: educationalQualifications,
        },
      });
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    if (!name.trim()) return false;
    if (isAITemplateType) {
      // AI template needs at least one context field
      return !!(titleContext || descriptionContext || requirementsContext || benefitsContext || educationalContext);
    } else {
      // Regular template needs a title
      return !!title.trim();
    }
  };

  const addRequirement = () => {
    if (requirementInput.trim()) {
      setRequirements([...requirements, requirementInput.trim()]);
      setRequirementInput('');
    }
  };

  const removeRequirement = (index: number) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const addBenefit = () => {
    if (benefitInput.trim()) {
      setBenefits([...benefits, benefitInput.trim()]);
      setBenefitInput('');
    }
  };

  const removeBenefit = (index: number) => {
    setBenefits(benefits.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {template ? 'Edit Template' : 'Create Template'}
              </h3>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Physics Faculty"
                    required
                  />
                </div>

                {/* AI Template Toggle */}
                <div className={`p-3 rounded-lg border ${isAITemplateType ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isAITemplateType}
                        onChange={(e) => setIsAITemplateType(e.target.checked)}
                        disabled={!!template} // Can't change type when editing
                        className="sr-only"
                      />
                      <div className={`w-10 h-6 rounded-full shadow-inner transition-colors ${isAITemplateType ? 'bg-purple-600' : 'bg-gray-300'} ${template ? 'opacity-50' : ''}`}></div>
                      <div className={`absolute w-4 h-4 bg-white rounded-full shadow top-1 transition-transform ${isAITemplateType ? 'translate-x-5' : 'translate-x-1'}`}></div>
                    </div>
                    <div className="ml-3">
                      <span className={`text-sm font-medium ${isAITemplateType ? 'text-purple-700' : 'text-gray-700'}`}>
                        AI Template
                      </span>
                      <p className="text-xs text-gray-500">
                        {isAITemplateType 
                          ? 'Define context prompts for AI to generate job content' 
                          : 'Define static content that will be copied to new jobs'}
                      </p>
                    </div>
                    {isAITemplateType && (
                      <svg className="w-5 h-5 ml-auto text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    )}
                  </label>
                </div>

                {/* AI Template Context Fields */}
                {isAITemplateType ? (
                  <>
                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-purple-700">
                          <strong>AI Context Prompts</strong> - These prompts guide AI when generating job content
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setTitleContext(defaultPrompts.title);
                            setDescriptionContext(defaultPrompts.description);
                            setRequirementsContext(defaultPrompts.requirements);
                            setBenefitsContext(defaultPrompts.benefits);
                            setEducationalContext(defaultPrompts.educational);
                          }}
                          className="text-xs text-purple-600 hover:text-purple-800 underline"
                        >
                          Reset to defaults
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title Context
                      </label>
                      <textarea
                        value={titleContext}
                        onChange={(e) => setTitleContext(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        placeholder="e.g., Generate a job title for a Physics faculty position focused on JEE/NEET preparation"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description Context
                      </label>
                      <textarea
                        value={descriptionContext}
                        onChange={(e) => setDescriptionContext(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        placeholder="e.g., Create a comprehensive job description for a Physics teacher at a coaching institute..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Requirements Context
                      </label>
                      <textarea
                        value={requirementsContext}
                        onChange={(e) => setRequirementsContext(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        placeholder="e.g., List requirements for a Physics faculty: educational qualifications, teaching experience..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Benefits Context
                      </label>
                      <textarea
                        value={benefitsContext}
                        onChange={(e) => setBenefitsContext(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        placeholder="e.g., Generate benefits typical for coaching institute faculty..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Educational Qualifications Context
                      </label>
                      <textarea
                        value={educationalContext}
                        onChange={(e) => setEducationalContext(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        placeholder="e.g., Specify educational requirements: Post-graduate in Physics, preference for IIT/NIT graduates"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Regular Template Fields */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Job Title *
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="e.g., Physics Faculty for JEE/NEET"
                        required={!isAITemplateType}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Job description..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location
                      </label>
                      <input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="e.g., Kanpur, Uttar Pradesh"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Salary Min
                        </label>
                        <input
                          type="number"
                          value={salaryMin}
                          onChange={(e) => setSalaryMin(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="e.g., 70000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Salary Max
                        </label>
                        <input
                          type="number"
                          value={salaryMax}
                          onChange={(e) => setSalaryMax(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="e.g., 90000"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Job Type
                        </label>
                        <select
                          value={jobType}
                          onChange={(e) => setJobType(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="full-time">Full-time</option>
                          <option value="part-time">Part-time</option>
                          <option value="contract">Contract</option>
                          <option value="internship">Internship</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Experience Level
                        </label>
                        <select
                          value={experienceLevel}
                          onChange={(e) => setExperienceLevel(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="entry">Entry Level</option>
                          <option value="mid">Mid Level</option>
                          <option value="senior">Senior Level</option>
                          <option value="lead">Lead</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Requirements
                      </label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={requirementInput}
                          onChange={(e) => setRequirementInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Add requirement..."
                        />
                        <button
                          type="button"
                          onClick={addRequirement}
                          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                        >
                          Add
                        </button>
                      </div>
                      <div className="space-y-2">
                        {requirements.map((req, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm text-gray-700">{req}</span>
                            <button
                              type="button"
                              onClick={() => removeRequirement(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Benefits
                      </label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={benefitInput}
                          onChange={(e) => setBenefitInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Add benefit..."
                        />
                        <button
                          type="button"
                          onClick={addBenefit}
                          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                        >
                          Add
                        </button>
                      </div>
                      <div className="space-y-2">
                        {benefits.map((benefit, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm text-gray-700">{benefit}</span>
                            <button
                              type="button"
                              onClick={() => removeBenefit(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Educational Qualifications
                      </label>
                      <EducationalQualificationsSection
                        qualifications={educationalQualifications}
                        onChange={setEducationalQualifications}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isSaving || !isFormValid()}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : template ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
